import React, { useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { Ledger, StatusCell, Who } from '../shared/cells';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import { EM_DASH, TOTALS_ROUNDING_NOTE, fmt_count } from '../format';
import { cell_text, preview_layout, preview_tracks, totals_view } from './adapters';

const NUMBER_KINDS = ['money2', 'rupees', 'int', 'decimal', 'bool'];
const sort_type_for = (kind) => {
  if (NUMBER_KINDS.indexOf(kind) !== -1) return 'number';
  if (kind === 'datetime') return 'date';
  return 'text';
};

// A uuid in full, wrapping at its hyphens (never truncated: a value is never hidden).
const IdCell = ({ value }) => <span className="tp-mono tp-rp-id">{value}</span>;

const blank = (v) => v === null || v === undefined || v === '';
const is_zero = (text) => /^[-−+]?₹?0(\.0+)?$/.test(String(text).replace(/,/g, ''));
const is_neg = (text) => /^[-−]/.test(String(text));
const money_tone = (text) => {
  if (is_zero(text)) return 'zero';
  return is_neg(text) ? 'neg' : '';
};

// A label/value grid for the report's other columns (text, counts, dates).
const Fields = ({ cols, row }) => (
  <dl className="tp-rp-fields">
    {cols.map((c) => (
      <div key={c.key} className="tp-rp-field">
        <dt>{c.label}</dt>
        <dd>
          {c.is_id && !blank(row[c.key]) ? (
            <IdCell value={String(row[c.key])} />
          ) : (
            cell_text(c.kind, row[c.key])
          )}
        </dd>
      </div>
    ))}
  </dl>
);

/*
 * The preview's columns on the list-row pattern: who · details · money ledger · status (adapters
 * preview_tracks). Sorts of the composed values are offered in the Sort select.
 */
const track_columns = (t) => {
  const sort_of = (c) => ({ sort_key: c.key, label: c.label });
  const cols = [];
  if (t.identifier) {
    cols.push({
      key: 'who',
      role: 'identifier',
      label: t.identifier.label,
      sort_key: t.identifier.key,
      sorts: t.ids.map(sort_of),
      track: { compact: '24%', full: '22%' },
      render: (row) => (
        <Who
          name={cell_text(t.identifier.kind, row[t.identifier.key])}
          meta={t.ids.map((c) =>
            blank(row[c.key]) ? null : (
              <>
                {c.label} <IdCell value={String(row[c.key])} />
              </>
            )
          )}
        />
      )
    });
  }
  if (t.details.length) {
    cols.push({
      key: 'details',
      label: 'Details',
      sorts: t.details.map(sort_of),
      render: (row) => <Fields cols={t.details} row={row} />
    });
  }
  if (t.total || t.money.length) {
    const head = t.total || t.money[t.money.length - 1];
    const items = t.total ? t.money : t.money.slice(0, -1);
    cols.push({
      key: 'money',
      role: 'amount',
      label: head.label,
      align: 'right',
      sort_key: head.key,
      sorts: items.map(sort_of),
      track: { compact: '208px', full: '228px' },
      render: (row) => {
        const text = (c) => cell_text(c.kind, row[c.key]);
        return (
          <Ledger
            items={items.map((c) => ({
              label: c.label,
              value: text(c),
              tone: money_tone(text(c))
            }))}
            total={{
              label: head.label,
              value: text(head),
              tone: is_zero(text(head)) ? 'zero' : ''
            }}
          />
        );
      }
    });
  }
  if (t.status) {
    cols.push({
      key: 'status',
      role: 'status',
      label: t.status.label,
      sort_key: t.status.key,
      track: { compact: '112px', full: '132px' },
      render: (row) =>
        blank(row[t.status.key]) ? (
          EM_DASH
        ) : (
          <StatusCell
            chip={<StatusPill>{cell_text(t.status.kind, row[t.status.key])}</StatusPill>}
          />
        )
    });
  }
  return cols;
};

/*
 * the click-to-preview panel: the first rows of /reports/data?preview=1 with the whole report's
 * totals under them, and the draft banner.
 */
const PreviewPanel = ({ preview, on_close, on_retry }) => {
  const heading_ref = useRef(null);
  const [sort, setSort] = useState(null);
  const { title, state, table, error_text } = preview;

  // The panel opens below the cards; focus (and so the viewport) moves to it. A new report starts unsorted.
  useEffect(() => {
    if (heading_ref.current) heading_ref.current.focus();
    setSort(null);
  }, [title]);

  const laid = table ? preview_layout(table.columns, table.rows) : [];
  const columns = table ? track_columns(preview_tracks(laid)) : [];
  // The sort reads the raw columns: a money sort compares values, not "₹" text.
  const sort_columns = laid.map((c) => ({ sort_key: c.key, sort_type: sort_type_for(c.kind) }));
  const rows = table
    ? sort_rows(
        table.rows.map((r, i) => ({ ...r, preview_row: i })),
        sort_columns,
        sort
      )
    : [];
  const totals = table ? totals_view(table) : [];
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));

  return (
    <section className="tp-card tp-rp-preview" aria-labelledby="tp-rp-preview-title">
      <div className="tp-card-hd">
        <span className="tp-card-hd-main">
          <span
            className="tp-card-hd-title"
            id="tp-rp-preview-title"
            tabIndex={-1}
            ref={heading_ref}
          >
            {title}
          </span>
          {state === 'ready' ? (
            <span className="tp-card-hd-sub">
              Showing {fmt_count(rows.length)} of {fmt_count(table.total_rows)} rows. Download the
              report for every row.
            </span>
          ) : null}
        </span>
        <span className="tp-card-hd-acts">
          <button type="button" className="tp-btn-ghost tp-rp-close" onClick={on_close}>
            Close preview
          </button>
        </span>
      </div>
      {state === 'loading' ? <CustomLoading /> : null}
      {state === 'error' ? (
        <div className="tp-rp-preview-body">
          <LoadFailed text={error_text} on_retry={on_retry} />
        </div>
      ) : null}
      {state === 'ready' && table.draft.is_draft ? (
        <div className="tp-rp-preview-body">
          <div className="tp-banner tp-banner-warn" role="status">
            {table.draft.label}
          </div>
        </div>
      ) : null}
      {state === 'ready' ? (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.preview_row}
            emptyText="This report has no rows for the month."
            sort={sort}
            on_sort={on_sort}
            on_sort_set={setSort}
            card_below={660}
          />
          {totals.length ? (
            <div className="tp-rp-totals-wrap">
              <div className="tp-rp-totals-hd">Report totals</div>
              <dl className="tp-rp-totals" aria-label="Report totals">
                {totals.map((t) => (
                  <div key={t.key} className="tp-rp-total">
                    <dt>{t.label}</dt>
                    <dd className={t.text === EM_DASH || is_zero(t.text) ? 'tp-muted' : undefined}>
                      {t.text}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="tp-rp-note">{TOTALS_ROUNDING_NOTE}</p>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default PreviewPanel;
